import { Suspense } from 'react';

import { Route, Routes } from 'react-router';

import {
  ExternalAuthComplete,
  ExternalAuthHandler,
} from 'src/components/ExternalAuthHandler';
import { APP_ROUTES } from 'src/constants/routes';
import { AuthenticationGuard } from 'src/features/auth/Auth0/AuthGuard';
import { AppEnvironmentDetector } from 'src/features/config/AppEnvironmentDetector';
import { useWebVitals } from 'src/hooks/useWebVitals';

import { ErrorPageThemeRegistry } from './features/config/ErrorPageThemeRegistry';
import { ThemeRegistry } from './features/config/ThemeRegistry';
import { FpProvider } from './features/guestApp/FpProvider';
import { NotFound } from './pages/404';
import { ServerError } from './pages/500';
import { ServiceUnavailable } from './pages/503';
import { ZoomErrorBoundary } from './pages/zoom/_components/ZoomErrorBoundary';
import {
  AuthenticationHandlerView,
  CATermsContent,
  ConsentPage,
  MeetRoutes,
  SupportTermsContent,
  TeamsRoutes,
  TermsOfServiceContent,
  WebAppRoutes,
  WebexRoutes,
  ZoomRoutes,
} from './routerUtils';

import { PageLoader } from '@/components/PageLoader';
import { TokenRefreshProvider } from '@/features/auth/external/TokenRefreshProvider';

const App = () => {
  useWebVitals();

  return (
    <TokenRefreshProvider>
      <AppEnvironmentDetector>
        <Routes>
          <Route
            element={
              <Suspense fallback={<PageLoader />}>
                <WebAppRoutes />
              </Suspense>
            }
            path={`${APP_ROUTES.WEB}/*`}
          />
          <Route
            element={
              <Suspense fallback={<PageLoader />}>
                <MeetRoutes />
              </Suspense>
            }
            path={`${APP_ROUTES.MEET}/*`}
          />
          <Route
            element={
              <ZoomErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <ZoomRoutes />
                </Suspense>
              </ZoomErrorBoundary>
            }
            path={`${APP_ROUTES.ZOOM}/*`}
          />
          <Route
            element={
              <Suspense fallback={<PageLoader />}>
                <WebexRoutes />
              </Suspense>
            }
            path={`${APP_ROUTES.WEBEX}/*`}
          />
          <Route
            element={
              <Suspense fallback={<PageLoader />}>
                <TeamsRoutes />
              </Suspense>
            }
            path={`${APP_ROUTES.TEAMS}/*`}
          />
          <Route
            element={
              <AuthenticationGuard component={AuthenticationHandlerView} />
            }
            path={APP_ROUTES.TEAMS_AUTH}
          />
          <Route
            element={
              <Suspense fallback={<PageLoader />}>
                <TermsOfServiceContent />
              </Suspense>
            }
            path={APP_ROUTES.TERMS}
          />
          <Route
            element={
              <Suspense fallback={<PageLoader />}>
                <CATermsContent />
              </Suspense>
            }
            path={APP_ROUTES.CA_TERMS}
          />
          <Route
            element={
              <Suspense fallback={<PageLoader />}>
                <SupportTermsContent />
              </Suspense>
            }
            path={APP_ROUTES.SUPPORT_TERMS}
          />
          <Route
            element={
              <Suspense
                fallback={<PageLoader className="flex-col">Loading</PageLoader>}
              >
                <FpProvider>
                  <ThemeRegistry forceTheme="light">
                    <ConsentPage />
                  </ThemeRegistry>
                </FpProvider>
              </Suspense>
            }
            path={APP_ROUTES.CONSENT}
          />
          <Route
            element={
              <ErrorPageThemeRegistry>
                <ServiceUnavailable />
              </ErrorPageThemeRegistry>
            }
            path={APP_ROUTES.SERVICE_UNAVAILABLE}
          />
          <Route
            element={<ExternalAuthHandler />}
            path={APP_ROUTES.EXTERNAL_AUTH}
          />
          <Route
            element={<ExternalAuthComplete />}
            path={APP_ROUTES.EXTERNAL_AUTH_COMPLETE}
          />
          <Route
            element={
              <ErrorPageThemeRegistry>
                <ServerError />
              </ErrorPageThemeRegistry>
            }
            path={APP_ROUTES.SERVER_ERROR}
          />
          <Route
            element={
              <ErrorPageThemeRegistry>
                <NotFound />
              </ErrorPageThemeRegistry>
            }
            path={APP_ROUTES.NOT_FOUND}
          />
        </Routes>
      </AppEnvironmentDetector>
    </TokenRefreshProvider>
  );
};

export default App;
